/**
 * Common types used across Trace components
 */

/**
 * 3D vector type for positions, sizes, and offsets.
 * Stored as `[x, y, z]` tuple — matches the Canopy spec wire format.
 */
export type Vec3 = [number, number, number];

/**
 * 4D vector type for quaternions and RGBA values.
 * Stored as `[x, y, z, w]` tuple — matches the Canopy spec wire format.
 */
export type Vec4 = [number, number, number, number];

/**
 * Color value in hex format
 * Examples: "#RRGGBB", "#RRGGBBAA"
 */
export type Color = string;

/**
 * Transform path to a node in the scene hierarchy.
 * Paths can reference SceneObjects or internal TransformNodes within loaded GLB assets.
 *
 * Path formats:
 * - "Name"            - Legacy: searches all objects by name (backwards compatible)
 * - "./Child"         - Relative: child of context object
 * - "./Child/Grand"   - Relative: descendant path from context
 * - "../Sibling"      - Relative: parent's other child
 * - "../../Name"      - Relative: navigate up multiple levels
 * - "/Root/UI/Panel"  - Absolute: path from scene root
 * - "$self"           - Keyword: the context object itself
 * - "$parent"         - Keyword: direct parent of context
 * - "$root"           - Keyword: scene root object
 *
 * Paths can continue into internal GLB hierarchy after reaching a SceneObject:
 * - "./Model/Armature/Hand" - SceneObject child, then internal nodes
 *
 * Resolution priority: SceneObject children are matched first, then internal GLB nodes.
 *
 * Examples:
 * - "UI/Panel/Button"       - Legacy absolute path
 * - "./Button"              - Child named "Button" of current object
 * - "../OtherPanel/Button"  - Sibling's child
 * - "$parent"               - Direct parent object
 */
export type TransformPath = string;

/**
 * TransformPath keywords. As whole field values these are paths, never
 * property bindings, even though they share the `$name` shape with a
 * whole-field reference — a property named `self`, `parent` or `root` cannot
 * be addressed that way.
 */
export const TRANSFORM_PATH_KEYWORDS: ReadonlySet<string> = new Set(['$self', '$parent', '$root']);

/**
 * Unity WrapMode for animation playback
 */
export enum WrapMode {
  Once = "Once",
  Loop = "Loop",
  PingPong = "PingPong",
  ClampForever = "ClampForever"
}

/**
 * Content orientation
 */
export enum Orientation {
  Horizontal = "Horizontal",
  Vertical = "Vertical"
}

/**
 * Expected pose of an image-tracking anchor in the world.
 *
 * - `Horizontal`: marker lies flat on a surface (table, floor).
 * - `Vertical`: marker is mounted on a vertical surface (wall).
 * - `Free`: no constraint — the runtime accepts any detected pose.
 */
export enum AnchorOrientation {
  Horizontal = "Horizontal",
  Vertical = "Vertical",
  Free = "Free"
}

/**
 * Resolve the scene-root pitch (radians, rotation about the marker's local
 * X axis / image-right) implied by the authored orientation and pitch
 * fields.
 *
 * Orientation and pitch describe the same pose at different fidelities.
 * Orientation wins when concrete — `Vertical`/`Horizontal` are complete
 * semantic poses and the pitch field is ignored. `Free` or unset
 * orientation falls through to the authored `pitchDegrees`, which is
 * the absolute pitch about the local X axis.
 *
 *   - Vertical              → 0       (marker stands up, +Z normal toward camera)
 *   - Horizontal            → -π/2    (marker lies flat, +Z normal toward world +Y)
 *   - Free/unset + pitch    → pitchDegrees converted to radians
 *   - Free/unset, no pitch  → 0
 */
export function resolveAnchorPitchRadians(
    orientation: AnchorOrientation | undefined,
    pitchDegrees: number | undefined,
): number {
    if (orientation === AnchorOrientation.Vertical) return 0;
    if (orientation === AnchorOrientation.Horizontal) return -Math.PI / 2;
    return ((pitchDegrees ?? 0) * Math.PI) / 180;
}

/**
 * Axis identifier for movement/rotation constraints
 */
export type Axis = "x" | "y" | "z";

/**
 * Viewport safe area insets in pixels, measured from each edge of the canvas.
 * UI overlays (like AutoUI) should offset their positioning by these values
 * to avoid being obscured by editor panels, device notches, etc.
 */
export interface SafeAreaInsets {
  left: number;
  top: number;
  right: number;
  bottom: number;
}
